'use client';

import Link from 'next/link';
import { useCallback, useId, useState } from 'react';

import { GuaranteeStrip } from '@/components/shared/GuaranteeStrip';
import { BOOK_CALL_HREF } from '@/lib/constants';
import { hasLongHeadingWord, railHeadingSize } from '@/lib/rail-heading';
import { cn } from '@/lib/utils';
import type {
  ServiceFaqSectionData,
  ServiceFaqSocialData,
} from '@/sanity/types';

interface ServiceFaqProps {
  data: ServiceFaqSectionData;
}

type SocialPlatform = ServiceFaqSocialData['platform'];

/**
 * Inline SVG icons for the supported social platforms. All icons render at
 * `24×24` in a `50×50` bordered circle; `currentColor` lets the active
 * state color the glyph without a second import.
 */
const SOCIAL_ICON: Record<SocialPlatform, React.JSX.Element> = {
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  ),
  telegram: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22.93 2.17a1.25 1.25 0 00-1.27-.2L1.78 9.56a1.25 1.25 0 00.06 2.35l4.9 1.46 2.02 6.32a1.25 1.25 0 002.01.57l2.82-2.56 4.8 3.55a1.25 1.25 0 001.96-.76l3.3-16.86a1.25 1.25 0 00-.72-1.46zM9.86 14.3l-.4 3.8 1.4-3.57 8.66-7.83-9.66 7.6z" />
    </svg>
  ),
  viber: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.04 0C5.4 0 0 5.42 0 12.08c0 2.14.56 4.15 1.55 5.9L.02 24l6.2-1.62a12.05 12.05 0 005.82 1.51H12.05c6.64 0 12.04-5.42 12.04-12.08C24.09 5.42 18.69 0 12.04 0zm6.7 17.23c-.28.79-1.4 1.43-2.28 1.6-.6.12-1.38.21-4.02-.88-3.37-1.4-5.54-4.82-5.71-5.04-.17-.22-1.36-1.81-1.36-3.46 0-1.65.84-2.46 1.14-2.79.26-.29.7-.42 1.12-.42.14 0 .27 0 .38.01.35.02.53.03.76.59.29.7.98 2.44 1.07 2.62.09.18.18.42.05.65-.12.25-.23.36-.42.56-.18.2-.36.35-.54.56-.18.2-.36.41-.15.77.21.36.93 1.54 2 2.49 1.38 1.22 2.5 1.62 2.9 1.8.3.13.66.1.88-.14.28-.3.62-.8.96-1.28.24-.35.55-.39.87-.27.33.12 2.05.97 2.4 1.14.35.17.58.26.66.41.08.15.08.87-.2 1.66zM13.5 4.1c4.05 0 7.33 3.3 7.33 7.37a.55.55 0 11-1.1 0 6.26 6.26 0 00-6.23-6.27.55.55 0 010-1.1zm0 2.2a5.17 5.17 0 015.13 5.17.55.55 0 11-1.1 0 4.07 4.07 0 00-4.03-4.07.55.55 0 110-1.1zm0 2.2a2.97 2.97 0 012.93 2.97.55.55 0 11-1.1 0 1.87 1.87 0 00-1.83-1.87.55.55 0 010-1.1z" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
    </svg>
  ),
  email: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 4H2a1 1 0 00-1 1v14a1 1 0 001 1h20a1 1 0 001-1V5a1 1 0 00-1-1zm-1 2l-9 5.87L3 6h18zM3 18V7.25l8.45 5.52a1 1 0 001.1 0L21 7.25V18H3z" />
    </svg>
  ),
};

const PLATFORM_DEFAULT_LABEL: Record<SocialPlatform, string> = {
  whatsapp: 'Contact us on WhatsApp',
  telegram: 'Contact us on Telegram',
  viber: 'Contact us on Viber',
  instagram: 'Follow us on Instagram',
  phone: 'Call us',
  email: 'Email us',
};

/**
 * Service detail page FAQs section.
 *
 * Two-column layout on desktop: the heading + short description sit in
 * the top-left, the accordion fills the right column across both rows,
 * and a "Still have questions?" block pinned to the bottom-left holds
 * secondary copy, social icons, and two link CTAs. Mobile stacks in
 * reading order: heading → accordion → secondary block.
 *
 * Accordion uses the WAI-ARIA disclosure pattern — each question is a
 * <button aria-expanded aria-controls> pair tied to a <section
 * role="region" aria-labelledby> panel. Panels animate via max-height
 * + opacity and respect `prefers-reduced-motion` implicitly because
 * the animation is pure CSS and short (200ms). The plus glyph rotates
 * to an X when the panel is open.
 */
export function ServiceFaq({ data }: ServiceFaqProps) {
  const items = data.items ?? [];
  const [openIndex, setOpenIndex] = useState<number | null>(
    items.length > 0 ? 0 : null,
  );
  const id = useId();

  const toggle = useCallback((index: number) => {
    setOpenIndex((current) => (current === index ? null : index));
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (event.key === 'Escape') {
        setOpenIndex(null);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        const next = index + 1 < items.length ? index + 1 : 0;
        document.getElementById(`${id}-trigger-${next}`)?.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prev = index - 1 >= 0 ? index - 1 : items.length - 1;
        document.getElementById(`${id}-trigger-${prev}`)?.focus();
      } else if (event.key === 'Home') {
        event.preventDefault();
        document.getElementById(`${id}-trigger-0`)?.focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        document.getElementById(`${id}-trigger-${items.length - 1}`)?.focus();
      }
    },
    [id, items.length],
  );

  if (items.length === 0) return null;

  const socials = data.serviceFaqSocials ?? [];
  const heading = data.faqHeading ?? 'FAQs';
  const longHeadingWord = hasLongHeadingWord(heading);

  return (
    <section
      id="faq"
      aria-labelledby="service-faq-heading"
      data-section-id="service_faq"
      className="relative bg-foreground py-16 md:py-20 lg:py-24 xl:py-28"
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="flex flex-col gap-14 lg:grid lg:grid-cols-[minmax(320px,420px)_1fr] lg:grid-rows-[auto_1fr] lg:items-start lg:gap-16 xl:gap-24">
          {/* ---------- Left top: heading + short description ---------- */}
          <div className="lg:col-start-1 lg:row-start-1">
            <h2
              id="service-faq-heading"
              className={cn(
                'font-light tracking-[-0.02em] text-[#121212]',
                // The rule this section worked out first, now shared with
                // ServiceAbout and QuickAnswers. See rail-heading.ts.
                railHeadingSize('xl98', longHeadingWord, 'leading-[0.961]'),
              )}
            >
              {heading}
            </h2>
            {data.faqDescription && (
              <p className="mt-6 max-w-[380px] text-[16px] font-light leading-[1.36] text-[#3d3d3d] md:text-[18px] lg:text-[20px]">
                {data.faqDescription}
              </p>
            )}
          </div>

          {/* ---------- Right column: accordion ---------- */}
          <div className="lg:col-start-2 lg:row-span-2">
            <ul
              role="list"
              className="flex flex-col border-t border-[#cbcbcb]"
            >
              {items.map((item, index) => {
                const open = openIndex === index;
                const triggerId = `${id}-trigger-${index}`;
                const panelId = `${id}-panel-${index}`;
                const indexLabel = `${String(index + 1).padStart(2, '0')}/`;
                return (
                  <li
                    key={`${item.question}-${index}`}
                    className="border-b border-[#cbcbcb]"
                  >
                    <h3>
                      <button
                        id={triggerId}
                        type="button"
                        aria-expanded={open}
                        aria-controls={panelId}
                        onClick={() => toggle(index)}
                        onKeyDown={(event) => onKeyDown(event, index)}
                        data-analytics-cta={`service_faq_${index + 1}`}
                        data-analytics-location="service_faq"
                        className="flex w-full items-center justify-between gap-6 py-8 text-left transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 md:py-10 lg:py-12"
                      >
                        <span className="flex-1 font-light leading-[1.05] tracking-[-0.02em] text-[#121212] text-[24px] md:text-[30px] lg:text-[34px] xl:text-[40px] xl:tracking-[-0.8px]">
                          <span className="text-[#121212]">{indexLabel}</span>
                          <span>{item.question}</span>
                        </span>
                        <span
                          aria-hidden="true"
                          className={
                            'flex size-8 shrink-0 items-center justify-center transition-transform duration-200 ease-out ' +
                            (open ? 'rotate-45' : 'rotate-0')
                          }
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#4dd017"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            className="size-6"
                          >
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                        </span>
                      </button>
                    </h3>
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={triggerId}
                      hidden={!open}
                      className={
                        'grid overflow-hidden transition-all duration-200 ease-out ' +
                        (open
                          ? 'grid-rows-[1fr] pb-8 opacity-100 md:pb-10 lg:pb-12'
                          : 'grid-rows-[0fr] opacity-0')
                      }
                    >
                      <div className="overflow-hidden">
                        <p className="max-w-[720px] text-[16px] font-light leading-[1.36] text-[#3d3d3d] md:text-[18px] lg:text-[20px]">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* ---------- Left bottom: "Still have questions?" block ---------- */}
          <div className="lg:col-start-1 lg:row-start-2 lg:self-end lg:pt-20">
            <h3 className="font-light leading-[0.961] tracking-[-0.02em] text-[#121212] text-[28px] md:text-[34px] lg:text-[40px] lg:tracking-[-0.8px]">
              {data.serviceFaqSecondaryHeading ?? 'Still have questions?'}
            </h3>
            {data.serviceFaqSecondaryDescription && (
              <p className="mt-4 max-w-[400px] text-[15px] font-light leading-[1.36] text-[#3d3d3d] lg:text-[18px] xl:text-[20px]">
                {data.serviceFaqSecondaryDescription}
              </p>
            )}

            {socials.length > 0 && (
              <ul
                role="list"
                aria-label="Contact channels"
                className="mt-8 flex flex-wrap items-center gap-2.5"
              >
                {socials.map((social, index) => {
                  const isAccent = index === 0;
                  const label =
                    social.label ?? PLATFORM_DEFAULT_LABEL[social.platform];
                  return (
                    <li key={`${social.platform}-${index}`}>
                      <a
                        href={social.href}
                        aria-label={label}
                        target={
                          social.href.startsWith('http') ? '_blank' : undefined
                        }
                        rel={
                          social.href.startsWith('http')
                            ? 'noopener noreferrer'
                            : undefined
                        }
                        data-analytics-cta={`service_faq_social_${social.platform}`}
                        data-analytics-location="service_faq"
                        className={
                          'flex size-[50px] items-center justify-center rounded-full p-3 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ' +
                          (isAccent
                            ? 'bg-accent text-white'
                            : 'border border-[#a3a3a3] text-[#3d3d3d] hover:text-[#121212]')
                        }
                      >
                        <span className="block size-6">
                          {SOCIAL_ICON[social.platform]}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-4">
              {data.serviceFaqContactsLabel && (
                <Link
                  href={data.serviceFaqContactsHref ?? '#brief-form'}
                  data-analytics-cta="service_faq_contacts"
                  data-analytics-location="service_faq"
                  className="inline-flex items-center gap-1.5 transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                  <span className="text-[16px] font-normal uppercase tracking-[-0.02em] text-[#121212] underline decoration-solid lg:text-[20px]">
                    {data.serviceFaqContactsLabel}
                  </span>
                </Link>
              )}
              {data.serviceFaqBookCallLabel && (
                <Link
                  href={data.serviceFaqBookCallHref ?? BOOK_CALL_HREF}
                  data-analytics-cta="service_faq_book_call"
                  data-analytics-location="service_faq"
                  className="inline-flex items-center gap-1.5 transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                  <span className="text-[16px] font-normal uppercase tracking-[-0.02em] text-[#121212] underline decoration-solid lg:text-[20px]">
                    {data.serviceFaqBookCallLabel}
                  </span>
                </Link>
              )}
            </div>

            {/* Published delivery guarantees at the closing CTA block
                (finding conversion-h5ur7m). */}
            <GuaranteeStrip tone="light" className="mt-8" />
          </div>
        </div>
      </div>
    </section>
  );
}
